import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Declara as funções globais do GTM
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

/**
 * Inicializa o dataLayer se não existir (fallback para quando GTM demora para carregar)
 */
const ensureDataLayer = () => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    return true;
  }
  return false;
};

/**
 * Hook personalizado para tracking de navegação SPA com Google Tag Manager
 * Usa dataLayer para enviar eventos para GTM, que então distribui para GA4 e outras ferramentas
 */
export const useGoogleAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Aguarda um pouco para o GTM carregar completamente
    const timer = setTimeout(() => {
      if (ensureDataLayer()) {
        // Método correto: usar dataLayer para GTM
        const pageViewData = {
          event: 'page_view',
          page_location: window.location.href,
          page_path: location.pathname + location.search,
          page_title: document.title,
          page_referrer: document.referrer,
          timestamp: Date.now()
        };

        window.dataLayer.push(pageViewData);

    }
    }, 100); // Aguarda 100ms para o GTM carregar

    return () => clearTimeout(timer);
  }, [location]);

  // Função para tracking de eventos customizados via GTM dataLayer
  const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
    if (ensureDataLayer()) {
      const eventData = {
        event: eventName,
        timestamp: Date.now(),
        ...parameters
      };
      
      window.dataLayer.push(eventData);

    }
  };

  return { trackEvent };
};

/**
 * Hook para tracking de eventos específicos da aplicação
 * Usa nomes de eventos padronizados do GA4 para melhor integração
 */
export const useAnalyticsEvents = () => {
  const { trackEvent } = useGoogleAnalytics();

  const trackNameSearch = (name: string) => {
    trackEvent('search', {
      search_term: name,
      content_type: 'name_data',
      item_category: 'name_search'
    });
  };

  const trackRankingView = (type: 'estados' | 'municipios' | 'municipios_invertido') => {
    trackEvent('view_item_list', {
      item_list_name: `ranking_${type}`,
      item_category: 'rankings',
      content_type: 'ranking_data'
    });
  };

  const trackComparison = (names: string[]) => {
    trackEvent('select_content', {
      content_type: 'comparison',
      item_id: names.join('_vs_'),
      custom_parameter_1: names.join(','),
      custom_parameter_2: names.length.toString()
    });
  };

  const trackGrouping = (names: string[]) => {
    trackEvent('select_content', {
      content_type: 'grouping',
      item_id: names.join('_group_'),
      custom_parameter_1: names.join(','),
      custom_parameter_2: names.length.toString()
    });
  };

  const trackCategoryView = (categoryName: string) => {
    trackEvent('view_item_list', {
      item_list_name: categoryName,
      item_category: 'name_categories',
      content_type: 'category_data'
    });
  };

  const trackNameDetails = (name: string, source?: string) => {
    trackEvent('view_item', {
      item_id: name.toLowerCase(),
      item_name: name,
      item_category: 'name_details',
      content_type: 'name_data',
      source: source || 'direct'
    });
  };

  return {
    trackNameSearch,
    trackRankingView,
    trackComparison,
    trackGrouping,
    trackCategoryView,
    trackNameDetails
  };
};